import React, { useState, useEffect } from 'react'
import { Box, Flex, Text, Tooltip, useToast } from '@chakra-ui/react'
import { useSortable } from '@dnd-kit/sortable'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Copy, Trash2, Plus, Video, Star, Play } from 'lucide-react'
import { createUniversalBlock, updateUniversalBlock } from './universalBlocksClient'
import useEmailDesignerStore from './emailDesignerStore'
import { loadGoogleFont, findFontEntry, HEADER_SHADOW_PRESETS, BLOCK_LABELS, SHADOW_STRIP_PRESETS, shadowStripGradient } from './blockDefaults'
import { SocialIconGlyph, SOCIAL_BRAND_COLORS } from './socialIcons'
import RichTextEditor from '../../../../components/RichTextEditor/RichTextEditor'
import { SIZE_WHITELIST, FONT_WHITELIST } from './quillEmailSetup'
import { preserveSpaces, HTML_BLOCK_RESET, extractYoutubeId } from './htmlExport'
import { hasCondition, describeCondition } from './emailConditions'
import { registerActiveEditor, clearActiveField } from './activeFieldTracker'
import { PRIMARY_BG } from '../../../../theme/brand';

// Compact single-bar toolbar shown on the widget while editing text inline —
// full Google-font list + numeric px sizes (applied inline, render on canvas).
const INLINE_TEXT_TOOLBAR = [
  [{ font: FONT_WHITELIST }, { size: SIZE_WHITELIST }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ align: [] }, { list: 'ordered' }, { list: 'bullet' }],
  ['link', 'clean'],
]

function resolveFontFamily(fontFamilyValue) {
  if (!fontFamilyValue) return undefined
  const entry = findFontEntry(fontFamilyValue)
  if (entry?.google) loadGoogleFont(entry.name)
  return fontFamilyValue
}

// The seeded text default is placeholder copy, not real content. When it's still
// the default (or empty), we clear it on edit so the user types into an empty
// field instead of deleting boilerplate first.
function isPlaceholderText(html) {
  const plain = String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
  return plain === '' || plain.toLowerCase() === 'type your text here...'
}

const ACCENT = PRIMARY_BG
const ACCENT_RING = `2px solid ${ACCENT}`
const ACCENT_SOFT = `1.5px solid ${ACCENT}55`

/* ─── Header bar (logo + nav) canvas preview ─── */
function headerLinkTextStyle(p) {
  const deco = []
  if (p.linkUnderline) deco.push('underline')
  if (p.linkStrike) deco.push('line-through')
  return {
    fontFamily: resolveFontFamily(p.linkFontFamily),
    fontSize: `${p.linkFontSize || 16}px`,
    color: p.linkColor || '#1A1A1A',
    letterSpacing: `${p.linkLetterSpacing || 0}px`,
    fontWeight: p.linkBold ? 700 : 400,
    fontStyle: p.linkItalic ? 'italic' : 'normal',
    textDecoration: deco.length ? deco.join(' ') : 'none',
    padding: `${p.linkPaddingV || 0}px ${p.linkPaddingH || 0}px`,
    display: 'inline-block',
    whiteSpace: 'nowrap',
  }
}

function HeaderLogo({ p }) {
  if (p.logoSrc) {
    return <img src={p.logoSrc} alt={p.logoAlt || ''} style={{ display: 'inline-block', border: 0, maxWidth: p.logoMaxWidth ? `${p.logoMaxWidth}px` : '180px', height: 'auto', verticalAlign: 'middle' }} />
  }
  return (
    <Box display="inline-flex" alignItems="center" justifyContent="center" px="18px" py="10px"
      border="1.5px dashed" borderColor="gray.300" borderRadius="6px" color="gray.400" fontSize="12px" fontWeight="600">
      Logo
    </Box>
  )
}

function HeaderLinks({ p }) {
  const items = (Array.isArray(p.links) ? p.links : []).map(l => {
    if (l.kind === 'image' && l.src) {
      return <img key={l.id} src={l.src} alt={l.alt || ''} style={{ display: 'inline-block', maxHeight: `${(p.linkFontSize || 16) + 12}px`, verticalAlign: 'middle', padding: `${p.linkPaddingV || 0}px ${p.linkPaddingH || 0}px` }} />
    }
    return <a key={l.id} href={l.url || '#'} onClick={e => e.preventDefault()} style={headerLinkTextStyle(p)}>{l.label || 'Link'}</a>
  })
  return <>{items}</>
}

function HeaderBarPreview({ p }) {
  const blockStyle = {
    padding: `${p.paddingTop || 0}px ${p.paddingRight || 0}px ${p.paddingBottom || 0}px ${p.paddingLeft || 0}px`,
    background: p.backgroundColor || 'transparent',
    border: (p.borderStyle && p.borderStyle !== 'none' && p.borderWidth) ? `${p.borderWidth}px ${p.borderStyle} ${p.borderColor}` : undefined,
    borderRadius: `${p.cornerRadius || 0}px`,
    boxShadow: HEADER_SHADOW_PRESETS[p.dropShadow] || undefined,
  }
  const valign = ({ top: 'flex-start', middle: 'center', bottom: 'flex-end' })[p.linkVAlign] || 'center'
  const gap = `${p.logoLinkSpacing || 0}px`
  const linkJustify = ({ left: 'flex-start', center: 'center', right: 'flex-end' })

  if (p.desktopLayout === 'links_only') {
    const j = p.linkHAlign === 'manual' ? linkJustify[p.linkAlignment] : 'center'
    return <Box style={blockStyle}><Flex wrap="wrap" align="center" justify={j}><HeaderLinks p={p} /></Flex></Box>
  }
  if (p.desktopLayout === 'logo_stacked' || p.desktopLayout === 'logo_centered') {
    const centered = p.desktopLayout === 'logo_centered'
    const logoJ = centered ? 'center' : linkJustify[p.logoAlignment] || 'flex-start'
    // ET_032: `centered` previously forced the links to center too, so Manual Link
    // Alignment had no effect on the logo_centered layout. Manual now wins; only the
    // automatic case falls back to centre.
    const linkJ = (p.linkHAlign === 'manual' ? linkJustify[p.linkAlignment] : (centered ? 'center' : linkJustify[p.logoAlignment])) || 'flex-start'
    return (
      <Box style={blockStyle}>
        <Flex direction="column" style={{ rowGap: gap }}>
          <Flex justify={logoJ}><HeaderLogo p={p} /></Flex>
          <Flex wrap="wrap" align="center" justify={linkJ}><HeaderLinks p={p} /></Flex>
        </Flex>
      </Box>
    )
  }
  // logo_inline: logo (aligned per logoAlignment within its cell) + links on the right
  const inlineLogoJ = linkJustify[p.logoAlignment] || 'flex-start'
  // ET_032 (second pass): reading the manual value was not enough — the links cell
  // had no `flex`, so it shrank to exactly its content width and `justify` had no
  // free space to distribute. Left/center/right all rendered identically. Both cells
  // now grow, so each owns half the leftover space and its justify is observable.
  // The default still looks unchanged: logo flush left, links flush right.
  const inlineLinkJ = (p.linkHAlign === 'manual' ? linkJustify[p.linkAlignment] : 'flex-end') || 'flex-end'
  return (
    <Box style={blockStyle}>
      <Flex align={valign} wrap="wrap" style={{ columnGap: gap, rowGap: '6px' }}>
        <Flex flex="1 1 auto" align="center" justify={inlineLogoJ}><HeaderLogo p={p} /></Flex>
        <Flex flex="1 1 auto" align="center" wrap="wrap" justify={inlineLinkJ}><HeaderLinks p={p} /></Flex>
      </Flex>
    </Box>
  )
}

/* ─── Story canvas preview (auto-advancing carousel like it plays in email) ─── */
/**
 * ET_020 / ET_023: the title used to be an absolutely-positioned overlay across the
 * bottom of the poster, and the play badge a circle floating in the middle. Neither
 * survives delivery — Gmail strips `position`, so in the sent mail both detached
 * from the image and rendered as loose fragments underneath it. htmlExport therefore
 * settled on a caption BAR flush beneath the poster, sharing its bottom corners, and
 * the designer kept showing the overlay: the editor was promising a layout the email
 * could never produce, which is what both bugs describe.
 *
 * The canvas now renders the same caption bar the exporter emits — badge and title in
 * one strip — so designer, Preview and inbox agree. Keep this in step with
 * renderStory() in htmlExport.js and the `story` case in ampEmailExport.js.
 *
 * The progress segments stay: they are an editor-only affordance showing how many
 * pages the story has, and amp-carousel draws its own indicators in the real email.
 */
function StoryCanvasPreview({ p }) {
  const slides = (p.slides && p.slides.length) ? p.slides : (p.poster ? [{ img: p.poster, alt: p.storyTitle }] : [])
  const [idx, setIdx] = useState(0)
  useEffect(() => {
    if (!p.autoplay || slides.length < 2) return
    const ms = Math.max(1200, (Number(p.autoplayDelay) || 3.5) * 1000)
    const t = setInterval(() => setIdx(i => (i + 1) % slides.length), ms)
    return () => clearInterval(t)
  }, [p.autoplay, p.autoplayDelay, slides.length])
  const cur = slides[Math.min(idx, slides.length - 1)] || slides[0]
  const radius = `${p.borderRadius || 0}px`
  const hasCaption = Boolean(p.showPlayBadge || (p.showTitle && p.storyTitle))
  return (
    <Box w={`${p.width || 100}%`} maxW="300px" mx="auto"
      borderRadius={radius} overflow="hidden" boxShadow="0 6px 20px rgba(0,0,0,0.18)">
      <Box position="relative" bg="gray.900" fontSize={0} lineHeight={0}>
        {cur?.img
          ? <img src={cur.img} alt={cur.alt || p.storyTitle || 'Story'} style={{ width: '100%', display: 'block', aspectRatio: '9 / 16', objectFit: 'cover' }} />
          : <Box style={{ width: '100%', aspectRatio: '9 / 16' }} bg="gray.700" />}
        {/* progress segments (current slide highlighted) */}
        {slides.length > 1 && (
          <Flex position="absolute" top="8px" left="8px" right="8px" gap="3px">
            {slides.slice(0, 10).map((_, i) => (
              <Box key={i} flex={1} h="3px" borderRadius="full" bg={i === idx ? 'whiteAlpha.900' : 'whiteAlpha.500'} transition="background 0.3s" />
            ))}
          </Flex>
        )}
      </Box>
      {hasCaption && (
        <Flex align="center" gap="6px" px="12px" py="9px" bg="#1f2937"
          borderBottomRadius={radius} fontFamily="Arial, Helvetica, sans-serif">
          {p.showPlayBadge && <Play size={12} color="#fff" fill="#fff" style={{ flexShrink: 0 }} />}
          {p.showTitle && p.storyTitle && (
            <Text fontSize="14px" fontWeight="700" color="white" noOfLines={2}>{p.storyTitle}</Text>
          )}
        </Flex>
      )}
    </Box>
  )
}

/* ─── Countdown timer canvas preview (live-ticking, mirrors the exported GIF) ─── */
function CountdownCanvasPreview({ p }) {
  const units = [
    p.showDays && { k: 'd', label: p.labelDays || 'Days' },
    p.showHours && { k: 'h', label: p.labelHours || 'Hours' },
    p.showMins && { k: 'm', label: p.labelMins || 'Mins' },
    p.showSecs && { k: 's', label: p.labelSecs || 'Secs' },
  ].filter(Boolean)
  const targetMs = p.targetDate ? new Date(p.targetDate).getTime() : (Date.now() + 7 * 86400 * 1000)
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  const sec = Math.max(0, Math.floor(((isNaN(targetMs) ? Date.now() : targetMs) - now) / 1000))
  const vals = { d: Math.floor(sec / 86400), h: Math.floor((sec % 86400) / 3600), m: Math.floor((sec % 3600) / 60), s: sec % 60 }
  const sz = { sm: { f: '26px', lf: '8.5px', p: '6px 8px' }, md: { f: '34px', lf: '10px', p: '8px 12px' }, lg: { f: '44px', lf: '11px', p: '10px 16px' } }[p.size] || { f: '34px', lf: '10px', p: '8px 12px' }
  const two = n => String(Math.min(99, Math.max(0, n))).padStart(2, '0')
  return (
    <Box display="inline-flex" alignItems="flex-start"
      style={{ background: p.boxColor || '#ffffff', borderRadius: '8px', padding: sz.p }}>
      {units.map((u, i) => (
        <React.Fragment key={u.k}>
          <Box textAlign="center" px="6px">
            <Box style={{ fontFamily: "'Courier New', monospace", fontWeight: 800, fontSize: sz.f, lineHeight: 1, color: p.digitColor || '#111827', letterSpacing: '1px' }}>{two(vals[u.k])}</Box>
            {p.showLabels && <Box style={{ fontSize: sz.lf, letterSpacing: '1px', textTransform: 'uppercase', color: p.labelColor || '#6B7280', marginTop: '6px', fontWeight: 600 }}>{u.label}</Box>}
          </Box>
          {i < units.length - 1 && <Box style={{ fontSize: sz.f, fontWeight: 800, color: p.separatorColor || p.digitColor || '#111827', lineHeight: 1, paddingTop: '1px' }}>:</Box>}
        </React.Fragment>
      ))}
    </Box>
  )
}

/* ─── AMP Form field preview ─── */
function FormFieldPreview({ f, accent }) {
  const labelEl = <Text fontSize="12px" fontWeight="600" color="gray.700" mb="4px">{f.label}{f.required && <Text as="span" color="red.400"> *</Text>}</Text>
  const inputStyle = { width: '100%', border: '1px solid #d1d5db', borderRadius: '7px', padding: '8px 10px', fontSize: '13px', background: '#fff', color: '#374151' }
  let control
  if (f.type === 'textarea') control = <textarea disabled rows={2} placeholder="Type here…" style={{ ...inputStyle, resize: 'none' }} />
  // ET_025: this rendered `<select disabled>` holding only options[0], so however
  // many options you configured the designer showed one and would not open —
  // exactly the reported symptom, even though the export lists them all. List every
  // option, and leave it enabled so the author can check the values are right.
  // onMouseDown stops the click bubbling to the block-select handler.
  else if (f.type === 'select') control = (
    <select
      style={{ ...inputStyle, cursor: 'pointer' }}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      onChange={e => e.stopPropagation()}
      defaultValue=""
    >
      {(f.options && f.options.length)
        ? f.options.map((o, i) => <option key={`${o}_${i}`} value={o}>{o}</option>)
        : <option value="">Choose…</option>}
    </select>
  )
  else if (f.type === 'rating') control = (
    <Flex gap={1.5}>{[1, 2, 3, 4, 5].map(n => (
      <Box key={n} w="30px" h="30px" borderRadius="7px" border="1px solid" borderColor="gray.300" display="flex" alignItems="center" justifyContent="center" fontSize="12px" fontWeight="700" color="gray.500">{n}</Box>
    ))}</Flex>
  )
  else control = <input disabled type={f.type === 'email' ? 'email' : 'text'} placeholder={f.type === 'email' ? 'you@example.com' : 'Your answer'} style={inputStyle} />
  return <Box mb={3}>{labelEl}{control}</Box>
}

/* ─── Block preview renderer ─── */
export function BlockPreview({ block, compact = false }) {
  const p = block.props
  const scale = compact ? 0.85 : 1
  // Link colour comes from page settings so canvas anchors match the sent email.
  const emailSettings = useEmailDesignerStore(s => s.emailSettings)

  switch (block.type) {
    case 'headerBar':
      return <HeaderBarPreview p={p} />

    case 'ampForm': {
      const accent = p.accentColor || ACCENT
      return (
        <Box style={{
          padding: `${p.paddingTop || 0}px ${p.paddingRight || 0}px ${p.paddingBottom || 0}px ${p.paddingLeft || 0}px`,
          background: p.backgroundColor || 'transparent',
        }}>
          {/* ET_028: the card is now its own settable surface (cardBackground) instead
              of a hardcoded white that silently overrode the author's colour on export.
              Opaque only — email cannot use rgba (Outlook ignores it). */}
          <Box borderRadius={`${p.borderRadius || 0}px`} border="1px solid" borderColor={`${accent}33`} bg={p.cardBackground || '#ffffff'} p={4}>
            {p.title && <Text fontSize="16px" fontWeight="800" color="gray.800" mb={1}>{p.title}</Text>}
            {p.intro && <Text fontSize="12px" color="gray.500" mb={3}>{p.intro}</Text>}
            {(p.fields || []).map(f => <FormFieldPreview key={f.id} f={f} accent={accent} />)}
            <Box display="inline-block" px="20px" py="9px" borderRadius="8px" bg={accent} color="white" fontSize="13px" fontWeight="700">
              {p.submitLabel || 'Submit'}
            </Box>
            <Text fontSize="9.5px" color="gray.400" mt={2}>⚡ Interactive in Gmail &amp; Yahoo · a static version shows elsewhere</Text>
          </Box>
        </Box>
      )
    }

    case 'story': {
      const pad = `${p.paddingTop || 0}px ${p.paddingRight || 0}px ${p.paddingBottom || 0}px ${p.paddingLeft || 0}px`
      const justify = { left: 'flex-start', center: 'center', right: 'flex-end' }[p.alignment] || 'center'
      if (!p.storyId) {
        return (
          <Box style={{ padding: pad, background: p.backgroundColor || 'transparent' }}>
            <Flex direction="column" align="center" justify="center" gap={2} py={8} borderRadius="12px"
              border="2px dashed" borderColor="pink.200" color="pink.400" bg="pink.50">
              <Play size={22} />
              <Text fontSize="12px" fontWeight="700">Select a Story</Text>
              <Text fontSize="10px" color="gray.400">Pick a published story in the panel →</Text>
            </Flex>
          </Box>
        )
      }
      return (
        <Box style={{ padding: pad, background: p.backgroundColor || 'transparent' }}>
          <Flex justify={justify}>
            <StoryCanvasPreview p={p} />
          </Flex>
          {p.showCta && (
            <Flex justify={justify} mt={2}>
              {/* Must read the same props as the exporters — hardcoding the colour
                  here made the ET_023 setting apply in Preview but not in the editor. */}
              <Box px="18px" py="8px" borderRadius="8px"
                bg={p.ctaBackgroundColor || ACCENT}
                color={p.ctaTextColor || '#ffffff'}
                fontSize="13px" fontWeight="700">
                ► {p.ctaLabel || 'View Story'}
              </Box>
            </Flex>
          )}
        </Box>
      )
    }

    case 'countdown': {
      const pad = `${p.paddingTop || 0}px ${p.paddingRight || 0}px ${p.paddingBottom || 0}px ${p.paddingLeft || 0}px`
      const justify = { left: 'flex-start', center: 'center', right: 'flex-end' }[p.alignment] || 'center'
      const noUnits = !p.showDays && !p.showHours && !p.showMins && !p.showSecs
      return (
        <Box style={{ padding: pad, background: p.backgroundColor || 'transparent' }}>
          <Flex justify={justify}>
            {noUnits
              ? <Text fontSize="11px" color="gray.400">Enable at least one unit in the panel →</Text>
              : <CountdownCanvasPreview p={p} />}
          </Flex>
        </Box>
      )
    }

    case 'dropShadow': {
      const h = (SHADOW_STRIP_PRESETS[p.shadowColor] || SHADOW_STRIP_PRESETS.medium).height
      return (
        <Box style={{
          padding: `${p.paddingTop || 0}px ${p.paddingRight || 0}px ${p.paddingBottom || 0}px ${p.paddingLeft || 0}px`,
          background: p.backgroundColor || 'transparent',
        }}>
          <Box style={{ height: `${h}px`, background: shadowStripGradient(p.shadowColor) }} />
        </Box>
      )
    }

    case 'heading': {
      const hFont = resolveFontFamily(p.fontFamily)
      return (
        <Box style={{
          padding: `${p.paddingTop * scale}px ${p.paddingRight * scale}px ${p.paddingBottom * scale}px ${p.paddingLeft * scale}px`,
          background: p.gradient || p.backgroundColor || 'transparent',
          textAlign: p.alignment,
        }}>
          <Box as={p.tag || 'h2'} style={{
            margin: 0, padding: 0,
            fontSize: `${Math.round(p.fontSize * scale)}px`,
            fontWeight: p.fontWeight, color: p.color, lineHeight: 1.3,
            fontFamily: hFont, whiteSpace: 'pre-wrap',
          }}>
            {p.content}
          </Box>
        </Box>
      )
    }

    case 'text': {
      const tFont = resolveFontFamily(p.fontFamily)
      const pad = `${p.paddingTop}px ${p.paddingRight}px ${p.paddingBottom}px ${p.paddingLeft}px`
      // Empty/placeholder text renders a faint hint so a cleared block stays clickable.
      if (isPlaceholderText(p.content)) {
        return (
          <Box style={{ padding: pad, background: p.backgroundColor || 'transparent' }}>
            <Box style={{ fontSize: `${p.fontSize}px`, textAlign: p.alignment, color: '#b8bfca', fontStyle: 'italic', fontFamily: tFont || undefined }}>
              Click to add text
            </Box>
          </Box>
        )
      }
      return (
        <Box style={{ padding: pad, background: p.backgroundColor || 'transparent' }}
          /* ET_031: while editing, Quill styles links itself, but once the block is
             deselected this plain render inherited the block's text colour and lost
             the underline — a link looked like ordinary black text. Style descendant
             anchors explicitly so the styling survives deselection. */
          sx={{ '& a': { color: emailSettings?.linkColor || '#2563eb', textDecoration: 'underline' } }}
          dangerouslySetInnerHTML={{
            // Same wrap rule as htmlExport's renderText, so the canvas and the
            // exported email break lines identically (ET_032/ET_033).
            __html: preserveSpaces(`<div style="font-size:${p.fontSize}px;font-weight:${p.fontWeight};color:${p.color};text-align:${p.alignment};line-height:${p.lineHeight};overflow-wrap:break-word;word-wrap:break-word;word-break:normal;${tFont ? `font-family:${tFont};` : ''}">${p.content}</div>`),
          }}
        />
      )
    }

    case 'image':
      return (
        <Box style={{
          padding: `${p.paddingTop}px ${p.paddingRight}px ${p.paddingBottom}px ${p.paddingLeft}px`,
          background: p.backgroundColor || 'transparent',
          textAlign: p.alignment || 'center',
        }}>
          {p.src ? (
            <img src={p.src} alt={p.alt || ''}
              style={{ maxWidth: `${p.width}%`, borderRadius: `${p.borderRadius}px`, display: 'inline-block', verticalAlign: 'top' }}
            />
          ) : (
            <Box
              display="inline-flex" flexDir="column"
              alignItems="center" justifyContent="center"
              w={compact ? '100%' : `${p.width}%`}
              h={compact ? '72px' : '160px'}
              borderRadius={`${p.borderRadius}px`}
              style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: '2px dashed #bae6fd' }}
            >
              <Text fontSize={compact ? '24px' : '32px'} lineHeight={1}>🖼️</Text>
              {!compact && <Text fontSize="11px" color="#0284c7" fontWeight="600" mt={2}>Set image URL in Properties →</Text>}
            </Box>
          )}
        </Box>
      )

    case 'button':
      return (
        <Box style={{ padding: `${p.paddingTop}px ${p.paddingRight}px ${p.paddingBottom}px ${p.paddingLeft}px`, textAlign: p.alignment }}>
          <Box as="span" display="inline-block" style={{
            padding: `${p.buttonPaddingV}px ${p.buttonPaddingH}px`,
            background: p.backgroundColor,
            color: p.textColor,
            borderRadius: `${p.borderRadius}px`,
            fontSize: `${p.fontSize}px`,
            fontWeight: p.fontWeight,
            cursor: 'default', lineHeight: 1.4,
          }}>
            {p.label}
          </Box>
        </Box>
      )

    case 'video': {
      // Shared with the exporter so the canvas and the email agree on which URLs have a
      // thumbnail: the local regex here only matched /embed/ URLs, so a plain
      // watch?v= or youtu.be link showed the empty "Set YouTube URL" placeholder on the
      // canvas while the export happily rendered a thumbnail for it.
      const videoId = extractYoutubeId(p.videoUrl)
      const thumbSrc = p.thumbnailUrl || (videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : '')
      return (
        <Box style={{
          padding: `${p.paddingTop}px ${p.paddingRight}px ${p.paddingBottom}px ${p.paddingLeft}px`,
          background: p.backgroundColor || 'transparent',
          textAlign: p.alignment || 'center',
        }}>
          {thumbSrc ? (
            <Box position="relative" display="inline-block" w={compact ? '100%' : 'auto'}>
              <img src={thumbSrc} alt="Video thumbnail"
                style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', display: 'block' }}
              />
              <Box
                position="absolute" top="50%" left="50%"
                transform="translate(-50%, -50%)"
                w={compact ? '32px' : '48px'} h={compact ? '32px' : '48px'}
                borderRadius="full"
                bg="rgba(0,0,0,0.6)"
                display="flex" alignItems="center" justifyContent="center"
              >
                <Text color="white" fontSize={compact ? '12px' : '18px'} ml="2px">►</Text>
              </Box>
            </Box>
          ) : (
            <Box
              display="inline-flex" flexDir="column"
              alignItems="center" justifyContent="center"
              w="100%"
              h={compact ? '72px' : '160px'}
              borderRadius="8px"
              style={{ background: '#1a1a2e', border: '2px dashed #374151' }}
            >
              <Video size={compact ? 20 : 32} color="#6b7280" />
              {!compact && <Text fontSize="11px" color="gray.400" fontWeight="500" mt={2}>Set YouTube URL in Properties →</Text>}
            </Box>
          )}
        </Box>
      )
    }

    case 'divider':
      return (
        <Box style={{ padding: `${p.paddingTop}px ${p.paddingRight}px ${p.paddingBottom}px ${p.paddingLeft}px` }}>
          <Box as="hr" style={{ border: 'none', borderTop: `${p.thickness}px ${p.style} ${p.color}`, margin: 0 }} />
        </Box>
      )

    case 'spacer':
      return (
        <Box
          style={{
            height: `${Math.max(compact ? 16 : Number(p.height), 8)}px`,
            background: p.backgroundColor || 'transparent',
            backgroundImage: 'repeating-linear-gradient(45deg, #f0f0f0 0, #f0f0f0 1px, transparent 0, transparent 50%)',
            backgroundSize: '8px 8px',
          }}
          display="flex" alignItems="center" justifyContent="center"
        >
          <Text fontSize="10px" color="gray.400" bg="white" px={2} borderRadius="3px">{p.height}px</Text>
        </Box>
      )

    case 'social': {
      const iconSize = compact ? 26 : Number(p.iconSize) || 36
      const platforms = [
        { key: 'facebook',  url: p.facebook,  enabled: p.facebookEnabled },
        { key: 'twitter',   url: p.twitter,   enabled: p.twitterEnabled },
        { key: 'instagram', url: p.instagram, enabled: p.instagramEnabled },
        { key: 'linkedin',  url: p.linkedin,  enabled: p.linkedinEnabled },
        { key: 'youtube',   url: p.youtube,   enabled: p.youtubeEnabled },
      ].filter(pl => pl.enabled)
      return (
        <Box style={{
          padding: `${p.paddingTop}px ${p.paddingRight}px ${p.paddingBottom}px ${p.paddingLeft}px`,
          background: p.backgroundColor || 'transparent',
        }}>
          <Flex
            justify={p.alignment === 'center' ? 'center' : p.alignment === 'right' ? 'flex-end' : 'flex-start'}
            gap={`${p.gap}px`} flexWrap="wrap"
          >
            {platforms.map(pl => (
              <Box key={pl.key} style={{ width: `${iconSize}px`, height: `${iconSize}px`, flexShrink: 0 }}>
                <SocialIconGlyph
                  platform={pl.key}
                  size={iconSize}
                  bgColor={p.iconColor || SOCIAL_BRAND_COLORS[pl.key]}
                  isCustomColor={Boolean(p.iconColor)}
                />
              </Box>
            ))}
          </Flex>
        </Box>
      )
    }

    case 'footer':
      return (
        <Box style={{
          padding: `${p.paddingTop}px ${p.paddingRight}px ${p.paddingBottom}px ${p.paddingLeft}px`,
          background: p.backgroundColor || 'transparent',
        }}
          /* Same anchor styling the export applies, so footer links don't look
             different here than in the sent email (ET_034). */
          sx={{ '& a': { color: emailSettings?.linkColor || '#2563eb', textDecoration: 'underline' } }}
          dangerouslySetInnerHTML={{
            __html: `<div style="font-size:${p.fontSize}px;color:${p.color};text-align:${p.alignment};line-height:1.6;">${p.content}</div>`,
          }}
        />
      )

    case 'html': {
      // ET_029: the canvas used to render the snippet bare — no padding, no font,
      // and inheriting Chakra's global reset — while the export wrapped it in
      // .xn-html-block with its own margins. Same markup, two different results.
      // Now both sides consume HTML_BLOCK_RESET, and the padding/font/wrap the
      // export puts on the cell are mirrored here.
      const hFont = resolveFontFamily(p.fontFamily)
      const resetSx = Object.entries(HTML_BLOCK_RESET).reduce((acc, [sel, decls]) => {
        acc[sel.split(',').map(s => `& ${s.trim()}`).join(', ')] = decls
          .split(';')
          .filter(d => d.trim())
          .reduce((o, d) => {
            const [prop, ...rest] = d.split(':')
            // CSS prop → camelCase for Chakra's sx
            o[prop.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = rest.join(':').trim()
            return o
          }, {})
        return acc
      }, {})
      return (
        <Box
          style={{
            padding: `${p.paddingTop || 0}px ${p.paddingRight || 0}px ${p.paddingBottom || 0}px ${p.paddingLeft || 0}px`,
            background: p.backgroundColor || 'transparent',
            fontFamily: hFont,
            overflowWrap: 'break-word',
            wordWrap: 'break-word',
            wordBreak: 'normal',
          }}
          sx={resetSx}
          dangerouslySetInnerHTML={{ __html: p.content }}
        />
      )
    }

    case 'columns':
      return <ColumnsPreview block={block} />

    default:
      return <Box p={4}><Text color="gray.400" fontSize="sm">Unknown: {block.type}</Text></Box>
  }
}

/* ─── Column drop zone ─── */
function ColumnDropZone({ col, parentBlockId }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `coldef::${col.id}`,
    data: { type: 'coldef', colDefId: col.id, parentBlockId },
  })
  const subBlocks = col.blocks || []

  return (
    <Box
      ref={setNodeRef} flex={1} minH="80px" minW={0} borderRadius="6px"
      border="1.5px dashed"
      borderColor={isOver ? ACCENT : 'gray.300'}
      bg={isOver ? `${ACCENT}08` : col.backgroundColor || 'transparent'}
      transition="all 0.15s" position="relative"
      /* overflow must stay visible so sub-block toolbars aren't clipped */
      overflow="visible"
    >
      <SortableContext items={subBlocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
        {subBlocks.length === 0 ? (
          <Box h="100%" minH="80px" display="flex" flexDir="column" alignItems="center" justifyContent="center" gap={1} opacity={0.5}>
            <Plus size={14} color="#9ca3af" />
            <Text fontSize="9px" color="gray.400" fontWeight="600">Drop here</Text>
          </Box>
        ) : (
          subBlocks.map(subBlock => (
            <SortableSubBlock key={subBlock.id} block={subBlock} colDefId={col.id} parentBlockId={parentBlockId} />
          ))
        )}
      </SortableContext>
    </Box>
  )
}

/**
 * Inline Quill editor for a text block. Extracted so top-level blocks and blocks
 * nested inside a layout column share ONE implementation — the nested path had no
 * editing branch at all, which is why text inside a Layout block could be selected
 * but never typed into (ET_036).
 */
function InlineTextEditor({ block, updateBlock }) {
  // ET_007: register this editor as the sidebar's insert-variable target for as long
  // as it is open. InlineTextEditor is only mounted for the block currently being
  // edited, so mount/unmount is exactly the right lifetime — no focus plumbing needed.
  useEffect(() => () => clearActiveField(block.id), [block.id])

  return (
    <Box
      style={{
        padding: `${block.props.paddingTop}px ${block.props.paddingRight}px ${block.props.paddingBottom}px ${block.props.paddingLeft}px`,
        background: block.props.backgroundColor || 'transparent',
      }}
      onClick={e => e.stopPropagation()}
      onDoubleClick={e => e.stopPropagation()}
      sx={{
        '.ql-editor': {
          fontSize: `${block.props.fontSize}px`,
          color: block.props.color,
          textAlign: block.props.alignment,
          lineHeight: block.props.lineHeight,
          fontFamily: resolveFontFamily(block.props.fontFamily),
          padding: '12px 4px',
        },
        '.ql-editor.ql-blank::before': {
          left: '4px', right: '4px', color: '#9ca3af', fontStyle: 'normal',
        },
        '.ql-toolbar.ql-snow': { padding: '9px 8px 8px' },
        '.ql-container.ql-snow': { border: 'none' },
      }}
    >
      <RichTextEditor
        value={block.props.content}
        onChange={html => updateBlock(block.id, { content: html })}
        onInsertHandleReady={fn => registerActiveEditor(block.id, fn)}
        toolbar={INLINE_TEXT_TOOLBAR}
        stickyToolbar
        autoFocus
        minHeight={48}
        placeholder="Type your text here…"
      />
    </Box>
  )
}

/* ─── Sortable sub-block in a column ─── */
function SortableSubBlock({ block, colDefId, parentBlockId }) {
  const [hovered, setHovered] = useState(false)
  const { selectedBlockId, selectBlock, removeBlock, duplicateBlock, editingTextId, setEditingText, updateBlock } = useEmailDesignerStore()
  const isSelected = selectedBlockId === block.id
  const isEditingText = editingTextId === block.id && block.type === 'text'
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
    data: { source: 'column-subblock', subBlockId: block.id, colDefId, parentBlockId },
  })

  const showToolbar = (hovered || isSelected) && !isDragging

  return (
    <Box
      ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      opacity={isDragging ? 0.4 : 1} position="relative"
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onClick={e => {
        e.stopPropagation()
        if (isEditingText) return
        selectBlock(block.id, { colDefId, parentBlockId })
        // Mirror the top-level behaviour so nested text opens its inline editor too.
        if (block.type === 'text') {
          if (isPlaceholderText(block.props.content)) updateBlock(block.id, { content: '' })
          setEditingText(block.id)
        }
      }}
      outline={isSelected ? ACCENT_RING : hovered ? ACCENT_SOFT : '1.5px solid transparent'}
      outlineOffset="-1px" cursor={isEditingText ? 'text' : 'pointer'} transition="outline 0.1s"
    >
      {/* Floating toolbar — rendered inside the block top-right so it's never clipped */}
      {showToolbar && (
        <Flex
          position="absolute" top="4px" right="4px"
          bg="white" borderRadius="6px"
          shadow="0 2px 10px rgba(0,0,0,0.15)"
          border="1px solid" borderColor="gray.200"
          overflow="hidden" zIndex={40}
        >
          {/* Block type label */}
          <Box
            px="6px" py="4px"
            bg={isSelected ? `${ACCENT}10` : 'gray.50'}
            borderRight="1px solid" borderColor="gray.100"
            display="flex" alignItems="center"
          >
            <Text fontSize="8px" fontWeight="800" color={isSelected ? ACCENT : 'gray.400'} textTransform="uppercase" letterSpacing="0.06em">
              {block.type}
            </Text>
          </Box>
          {/* Drag handle */}
          <Tooltip label="Drag" hasArrow placement="top" openDelay={600}>
            <Box {...attributes} {...listeners} px="6px" py="4px" cursor="grab" color="gray.400"
              _hover={{ bg: 'gray.50', color: 'gray.600' }} display="flex" alignItems="center" _active={{ cursor: 'grabbing' }}>
              <GripVertical size={11} />
            </Box>
          </Tooltip>
          <Box w="1px" bg="gray.100" />
          {/* Duplicate */}
          <Tooltip label="Duplicate" hasArrow placement="top" openDelay={400}>
            <Box px="6px" py="4px" cursor="pointer" color="gray.400"
              _hover={{ bg: 'gray.50', color: 'gray.700' }} display="flex" alignItems="center"
              onClick={e => { e.stopPropagation(); duplicateBlock(block.id) }}>
              <Copy size={11} />
            </Box>
          </Tooltip>
          <Box w="1px" bg="gray.100" />
          {/* Delete sub-block only */}
          <Tooltip label="Delete block" hasArrow placement="top" openDelay={400}>
            <Box px="6px" py="4px" cursor="pointer" color="gray.400"
              _hover={{ bg: 'red.50', color: 'red.500' }} display="flex" alignItems="center"
              onClick={e => { e.stopPropagation(); removeBlock(block.id) }}>
              <Trash2 size={11} />
            </Box>
          </Tooltip>
        </Flex>
      )}
      {isEditingText
        ? <InlineTextEditor block={block} updateBlock={updateBlock} />
        : <BlockPreview block={block} compact />}
    </Box>
  )
}

/* ─── Columns block preview ─── */
function ColumnsPreview({ block }) {
  const p = block.props
  const count = Number(p.columns) || 2
  const cols = Array.from({ length: count }, (_, i) => p.columnDefs?.[i] || { id: `_fb_${i}`, blocks: [], backgroundColor: '' })

  return (
    <Box style={{
      padding: `${p.paddingTop}px ${p.paddingRight}px ${p.paddingBottom}px ${p.paddingLeft}px`,
      background: p.backgroundColor || 'transparent',
    }}>
      <Flex gap={`${p.gap || 16}px`} align="stretch">
        {cols.map((col, i) => <ColumnDropZone key={col.id} col={col} parentBlockId={block.id} colIndex={i} />)}
      </Flex>
    </Box>
  )
}

/* ─── Main canvas block ─── */
function CanvasBlock({ block }) {
  const [hovered, setHovered] = useState(false)
  const { selectedBlockId, selectBlock, removeBlock, duplicateBlock, editingTextId, setEditingText, updateBlock, bumpUniversalRefresh } = useEmailDesignerStore()
  const toast = useToast()
  const isUniversal = Boolean(block.props?._universalId)

  // Save this block to the tenant's Universal library (or update the linked one),
  // then link the on-canvas block to it via _universalId so future edits sync.
  async function handleSaveUniversal(e) {
    e.stopPropagation()
    try {
      const cleanProps = { ...block.props }
      delete cleanProps._universalId
      delete cleanProps._universalName
      const payload = { data: { type: block.type, props: cleanProps } }
      if (block.props?._universalId) {
        await updateUniversalBlock({ id: block.props._universalId, ...payload })
        toast({ title: 'Universal block updated everywhere', status: 'success', duration: 2000 })
      } else {
        const name = window.prompt('Name this universal block', BLOCK_LABELS[block.type] || 'Saved block')
        if (!name) return
        const id = await createUniversalBlock({ name, kind: 'block', ...payload })
        updateBlock(block.id, { _universalId: id, _universalName: name })
        toast({ title: `Saved “${name}” to Universal`, status: 'success', duration: 2000 })
      }
      bumpUniversalRefresh()
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 3500 })
    }
  }
  const isSelected = selectedBlockId === block.id
  const isEditingText = editingTextId === block.id && block.type === 'text'
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
    data: { source: 'canvas', blockId: block.id },
  })

  return (
    <Box
      ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      position="relative" opacity={isDragging ? 0.4 : 1}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onClick={e => {
        e.stopPropagation()
        if (isEditingText) return
        selectBlock(block.id, null)
        // Selecting a text layer opens its inline toolbar (Klaviyo-style) and
        // clears placeholder copy so the user types into an empty field.
        if (block.type === 'text') {
          if (isPlaceholderText(block.props.content)) updateBlock(block.id, { content: '' })
          setEditingText(block.id)
        }
      }}
      outline={isSelected ? ACCENT_RING : hovered ? ACCENT_SOFT : '1.5px solid transparent'}
      outlineOffset="-1px" cursor={isEditingText ? 'text' : 'pointer'} transition="outline 0.1s"
    >
      {/* Hover/selected toolbar */}
      {(hovered || isSelected) && (
        <Flex
          position="absolute" top="-32px" right="6px"
          bg="white" borderRadius="8px"
          shadow="0 2px 12px rgba(0,0,0,0.12)"
          border="1px solid" borderColor="gray.200"
          overflow="hidden" zIndex={20}
        >
          {/* Block label */}
          <Box
            px={2} py="6px"
            bg={isSelected ? `${ACCENT}10` : 'gray.50'}
            borderRight="1px solid" borderColor="gray.200"
            display="flex" alignItems="center"
          >
            <Text fontSize="9px" fontWeight="800" color={isSelected ? ACCENT : 'gray.500'} textTransform="uppercase" letterSpacing="0.06em">
              {block.type}
            </Text>
            {/* hasCondition, not just `.field`: a half-filled rule is not emitted into
                the email, so badging it "IF" claimed a restriction that does not exist. */}
            {hasCondition(block.props?._condition) && (
              <Tooltip label={describeCondition(block.props._condition)} hasArrow placement="top">
                <Box ml={1.5} px={1} borderRadius="4px" bg="#db277718" color="#db2777" fontSize="8px" fontWeight="800">IF</Box>
              </Tooltip>
            )}
          </Box>
          <Tooltip label="Drag to reorder" hasArrow placement="top">
            <Box {...attributes} {...listeners} px="8px" py="6px" cursor="grab" color="gray.400"
              _hover={{ bg: 'gray.50', color: 'gray.700' }} display="flex" alignItems="center" _active={{ cursor: 'grabbing' }}>
              <GripVertical size={13} />
            </Box>
          </Tooltip>
          <Box w="1px" bg="gray.100" />
          <Tooltip label={isUniversal ? 'Update universal (syncs everywhere)' : 'Save as universal'} hasArrow placement="top">
            <Box px="8px" py="6px" cursor="pointer" color={isUniversal ? '#0d9488' : 'gray.400'}
              _hover={{ bg: '#0d948810', color: '#0d9488' }} display="flex" alignItems="center"
              onClick={handleSaveUniversal}>
              <Star size={13} fill={isUniversal ? '#0d9488' : 'none'} />
            </Box>
          </Tooltip>
          <Box w="1px" bg="gray.100" />
          <Tooltip label="Duplicate" hasArrow placement="top">
            <Box px="8px" py="6px" cursor="pointer" color="gray.400"
              _hover={{ bg: 'gray.50', color: 'gray.700' }} display="flex" alignItems="center"
              onClick={e => { e.stopPropagation(); duplicateBlock(block.id) }}>
              <Copy size={13} />
            </Box>
          </Tooltip>
          <Box w="1px" bg="gray.100" />
          <Tooltip label="Delete" hasArrow placement="top">
            <Box px="8px" py="6px" cursor="pointer" color="gray.400"
              _hover={{ bg: 'red.50', color: 'red.500' }} display="flex" alignItems="center"
              onClick={e => { e.stopPropagation(); removeBlock(block.id) }}>
              <Trash2 size={13} />
            </Box>
          </Tooltip>
        </Flex>
      )}

      {isEditingText
        ? <InlineTextEditor block={block} updateBlock={updateBlock} />
        : <BlockPreview block={block} />}
    </Box>
  )
}

export default CanvasBlock
